// src/components/Finanzas/ModalObservacionesMovimiento.jsx
// Modal de solo lectura que muestra observaciones de confirmacion y motivo de
// rechazo de un traslado o entrega. Se usa desde:
//   - MiBolsa.jsx → tabla "Movimientos de Dinero" (data ya hidratada).
//   - MiBolsa.jsx → accordion "Ver ledger completo" (data viene de fetch lazy
//     con obtenerEntrega/obtenerTrasladoOps a partir de m.origen.refId).
//   - TablaMovimientosCierre → mismo modal al pasar onVerObservaciones.
//
// Los campos de observaciones difieren entre traslado y entrega:
//   - Traslado: confirmacion.observaciones
//   - Entrega : confirmacion.observacionesReceptor
//
// El modelo NO devuelve `confirmacion.firmaDigital` (excluida por proteccion
// de PII biometrica en el backend — ver security-design.md).

import React from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { FaCheckCircle, FaBan } from 'react-icons/fa';
import { formatoFechaColombia } from '../../utils/fechasColombia';

const obtenerNombreUsuario = (u) => {
  if (!u) return '—';
  if (typeof u === 'string') return '—'; // ObjectId sin populate
  const nombre = u.nombre || u.nombres || '';
  const apellido = u.apellido || u.apellidos || '';
  const full = `${nombre} ${apellido}`.trim();
  return full || u.email || '—';
};

const ModalObservacionesMovimiento = ({ show, onHide, movimiento, tipo }) => {
  if (!show || !movimiento) return null;

  const esTraslado = tipo === 'traslado';
  const titulo = esTraslado
    ? `Observaciones del traslado ${movimiento.consecutivo || ''}`
    : `Observaciones de la entrega ${movimiento.consecutivo || ''}`;

  const observacionConfirmacion = esTraslado
    ? movimiento?.confirmacion?.observaciones
    : movimiento?.confirmacion?.observacionesReceptor;
  const fechaConfirmacion = movimiento?.confirmacion?.fechaConfirmacion;
  const confirmadoPor = movimiento?.confirmacion?.confirmadoPor;

  const motivoRechazo = movimiento?.rechazo?.motivoRechazo;
  const fechaRechazo = movimiento?.rechazo?.fechaRechazo;
  const rechazadoPor = movimiento?.rechazo?.rechazadoPor;

  const estado = movimiento?.estado || '—';
  const colorEstado =
    /CONFIRM/i.test(estado) ? 'success'
    : /RECHAZ/i.test(estado) ? 'danger'
    : /PENDIENTE/i.test(estado) ? 'warning'
    : /ANUL/i.test(estado) ? 'secondary'
    : 'info';

  const mostrarConfirmacion = !!(observacionConfirmacion || fechaConfirmacion);
  const mostrarRechazo = !!(motivoRechazo || fechaRechazo);
  const sinObservaciones = !mostrarConfirmacion && !mostrarRechazo;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>Estado actual: </strong>
          <Badge bg={colorEstado}>{String(estado).replace(/_/g, ' ')}</Badge>
        </p>

        {mostrarConfirmacion && (
          <div className="mb-3 p-3 bg-light border-start border-success border-4">
            <h6 className="text-success mb-2">
              <FaCheckCircle className="me-1" />
              Confirmación
            </h6>
            <p className="mb-1 small">
              <strong>Fecha: </strong>
              {fechaConfirmacion ? formatoFechaColombia(fechaConfirmacion) : '—'}
            </p>
            <p className="mb-1 small">
              <strong>Usuario que confirmó: </strong>
              {obtenerNombreUsuario(confirmadoPor)}
            </p>
            <p className="mb-0 small">
              <strong>Observación: </strong>
              {observacionConfirmacion
                ? <span>{observacionConfirmacion}</span>
                : <em className="text-muted">Sin observación registrada.</em>}
            </p>
          </div>
        )}

        {mostrarRechazo && (
          <div className="mb-3 p-3 bg-light border-start border-danger border-4">
            <h6 className="text-danger mb-2">
              <FaBan className="me-1" />
              Rechazo
            </h6>
            <p className="mb-1 small">
              <strong>Fecha: </strong>
              {fechaRechazo ? formatoFechaColombia(fechaRechazo) : '—'}
            </p>
            <p className="mb-1 small">
              <strong>Usuario que rechazó: </strong>
              {obtenerNombreUsuario(rechazadoPor)}
            </p>
            <p className="mb-0 small">
              <strong>Motivo: </strong>
              {motivoRechazo
                ? <span>{motivoRechazo}</span>
                : <em className="text-muted">Sin motivo registrado.</em>}
            </p>
          </div>
        )}

        {sinObservaciones && (
          <p className="text-muted fst-italic mb-0">
            Sin observaciones registradas para este movimiento.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalObservacionesMovimiento;
